import React, { useState } from 'react';
import './ViewTable.scss';
import { Button, Table } from 'antd';
import { VisitorRequestReview, Date as ReviewDate } from 'core/visitRequestReview/types';
import { REQUEST_TYPE_ACCESS, REQUEST_TYPE_GUARDING } from 'core/utils/value';
import { shiftSignature } from 'core/visitRequestReview/scheduleShifts';
import { format, isBefore, startOfDay, compareAsc } from 'date-fns';

interface ViewTableProps {
    data: VisitorRequestReview;
}

// How many upcoming shifts to show before the rest are collapsed away. Keeps the
// request view small for large bookings (weeks/months of shifts).
const PREVIEW_COUNT = 3;

const ViewTable: React.FC<ViewTableProps> = ({ data }) => {
    const [expanded, setExpanded] = useState(false);

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        const formattedDate = format(date, 'EEEE do MMMM');
        return formattedDate;
    };

    // Access requests don't assign a security officer, so the column is
    // meaningless for them. Guarding and visitor requests still schedule.
    const isAccessRequest = data.type === REQUEST_TYPE_ACCESS;

    // Guarding requests never assign a site access area, so that column is
    // meaningless for them (they still schedule a security officer).
    const isGuardingRequest = data.type === REQUEST_TYPE_GUARDING;

    // Access areas and visitor count are per shift and stored on the base row
    // (officer_index 0). The extra officer rows are copies with no site access,
    // so borrow those values from their shift's base row.
    const shiftInfo = new Map<string, { accessLocations?: string[] | undefined, visitorCount?: number | undefined }>();
    (data.dates || []).forEach((date) => {
        if ((date.officerIndex ?? 0) === 0) {
            shiftInfo.set(shiftSignature(date), {
                accessLocations: date.accessLocations,
                visitorCount: date.visitorCount,
            });
        }
    });

    const viewColumns = [
        {
            title: 'Dates Requested',
            dataIndex: 'date',
            key: 'date',
            render: (date: string) => formatDate(date),
        },
        ...(isGuardingRequest ? [] : [{
            title: 'Access Area(s)',
            key: 'accessLocations',
            render: (_: unknown, row: ReviewDate) => {
                // Access requests use a single Access Type (e.g. "Secure Data Hall")
                // that applies to all visitors/shifts. Show it in the access area
                // column, treating it the same as visitor-request access areas.
                if (isAccessRequest) {
                    return data.accessType || '—';
                }
                const locations = shiftInfo.get(shiftSignature(row))?.accessLocations ?? row.accessLocations;
                return locations && locations.length ? locations.join(', ') : '—';
            },
        }]),
        ...(isAccessRequest ? [] : [{
            title: 'Assigned Security Officer',
            dataIndex: 'securityOfficer',
            key: 'securityOfficer',
            render: (securityOfficer: string) => securityOfficer || 'Pending Schedule',
        }]),
        {
            title: 'Number of Visitors',
            key: 'visitorCount',
            render: (_: unknown, row: ReviewDate) => (
                shiftInfo.get(shiftSignature(row))?.visitorCount ?? row.visitorCount
            ),
        },
    ];

    // One row per officer-shift (each assigned officer is its own row), sorted
    // chronologically.
    const allShifts = [...(data.dates || [])]
        .sort((a, b) => compareAsc(new Date(a.date as string), new Date(b.date as string)));

    // Default view: the next few upcoming (today or later) shifts. If the whole
    // booking is in the past, fall back to the most recent shifts so the table
    // is never empty.
    const today = startOfDay(new Date());
    const upcomingShifts = allShifts.filter(
        (shift) => !isBefore(new Date(shift.date as string), today),
    );
    const previewShifts = upcomingShifts.length > 0
        ? upcomingShifts.slice(0, PREVIEW_COUNT)
        : allShifts.slice(-PREVIEW_COUNT);

    const visibleShifts = expanded ? allShifts : previewShifts;

    // Number hidden while collapsed (historic + further-out future shifts).
    const hiddenCount = allShifts.length - previewShifts.length;

    return (
        <div className="view-table-container">
            <Table
                rowKey="id"
                pagination={false}
                columns={viewColumns}
                dataSource={visibleShifts} />

            {hiddenCount > 0 && (
                <Button
                    type="link"
                    className="view-table-toggle"
                    onClick={() => setExpanded((prev) => !prev)}>
                    {expanded
                        ? 'Show fewer shifts ▴'
                        : `Show all shifts (${hiddenCount} more) ▾`}
                </Button>
            )}
        </div>
    );
};

ViewTable.propTypes = {
};

export default ViewTable;
